// image-upload.component.ts
import { Component, EventEmitter, Input, Output, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import imageCompression from 'browser-image-compression';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import { ModalComponent } from '../modal/modal.component';
import { AppImageComponent } from '../image/image';
import { ToastrService } from 'ngx-toastr';

const BYTES_PER_MB = 1024 * 1024;

type UploadStatus = 'ready' | 'rejected' | 'processing' | 'uploaded';
type RejectionReason = 'oversize';

type QueuedItem = {
  file: File;
  name: string;
  sizeMb: number;
  previewUrl?: string; // object URL for thumbnail
  status: UploadStatus;
  rejectionReason?: RejectionReason;
  batchIndex?: number;
};

export type PreparedBatch = {
  batchIndex: number;
  totalBatches: number;
  totalSizeMb: number;
  count: number;
  files: File[];
};

type QueueSnapshot = {
  valid: QueuedItem[];
  rejected: QueuedItem[];
  hasOversize: boolean;
};

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  imports: [ModalComponent, AppImageComponent, TranslocoPipe],
  standalone: true,
})
export class ImageUploadComponent implements OnDestroy {
  // ---- Inputs (list-oriented) ----
  @Input() shouldCompress = false;
  @Input() maxSizeInMb = 50;
  @Input() maxWidthOrHeight = 800;
  @Input() label = 'image-upload.upload-button';
  @Input() buttonSize: 'small' | 'medium' | 'large' = 'small';
  @Input() maxNumberOfImages: number|null = 1; // hard cap for this selection

  // ---- Output: single batch only ----
  @Output() imagesSelected = new EventEmitter<File[]>();
  @Output() batchReady = new EventEmitter<PreparedBatch>();

  // ---- Template refs ----
  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;

  // ---- UI state ----
  uploadModalOpened = false;
  isDraggingOver = false;
  isSubmitting = false;
  totalBatches = 0;
  private cancelRequested = false;

  // In-memory queue
  queue: QueuedItem[] = [];
  warningMessages: string[] = [];
  informationMessages: string[] = [];

  private currentBatches: QueuedItem[][] = [];
  private batchToastIds = new Map<number, number>();
  private completedBatches = 0;
  private activeBatchIndex: number | null = null;

  private readonly singleFileLimitMb = 50;
  private readonly batchLimitMb = 200;
  private lastWarningSignature = '';

  constructor(
    private readonly toastr: ToastrService,
    private readonly i18n: TranslocoService,
  ) {}

  get allowMultipleSelection(): boolean {
    return this.maxNumberOfImages === null || this.maxNumberOfImages > 1;
  }

  private toMegabytes(bytes: number): number {
    return bytes / BYTES_PER_MB;
  }

  // ---------- Modal ----------
  openUploadModal(): void {
    this.cancelRequested = false;
    this.isDraggingOver = false;
    this.uploadModalOpened = true;
  }
  closeUploadModal(): void {
    this.uploadModalOpened = false;
  }

  // ---------- Picker ----------
  triggerPick(): void { this.fileInputRef?.nativeElement.click(); }
  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.addToQueue(files);
    input.value = '';
  }

  // ---------- Drag & drop ----------
  onDragOver(ev: DragEvent): void { ev.preventDefault(); this.isDraggingOver = true; }
  onDragLeave(): void { this.isDraggingOver = false; }
  onDrop(ev: DragEvent): void {
    ev.preventDefault();
    this.isDraggingOver = false;
    const files = Array.from(ev.dataTransfer?.files ?? []);
    this.addToQueue(files);
  }

  // ---------- Queue helpers ----------
  private remainingSlots(): number {
    if (!this.maxNumberOfImages) return Number.POSITIVE_INFINITY;
    const used = this.queue.filter(item => item.status !== 'rejected').length;
    return Math.max(0, this.maxNumberOfImages - used);
  }

  private addToQueue(files: File[]): void {
    if (!files.length) return;

    // images only
    let images = files.filter(f => f.type.startsWith('image/'));
    if (!images.length) {
      this.showToast('warning', 'only-images');
      return;
    }

    const slots = this.remainingSlots();
    if (slots <= 0) {
      this.showToast('info', 'max-reached', { max: this.maxNumberOfImages });
      return;
    }
    if (images.length > slots) {
      this.showToast('info', 'only-n-added', { count: slots });
      images = images.slice(0, slots);
    }

    // dedupe (name+size)
    const existingKeys = new Set(this.queue.map(q => `${q.name}|${q.file.size}`));
    const added: QueuedItem[] = [];
    let hasOversized = false;

    for (const f of images) {
      const key = `${f.name}|${f.size}`;
      if (existingKeys.has(key)) continue;

      const sizeMb = this.toMegabytes(f.size);
      const previewUrl = URL.createObjectURL(f);
      const isOversize = sizeMb > this.singleFileLimitMb;
      added.push({
        file: f,
        name: f.name,
        sizeMb,
        previewUrl,
        status: isOversize ? 'rejected' : 'ready',
        rejectionReason: isOversize ? 'oversize' : undefined,
      });
      if (isOversize) {
        hasOversized = true;
      }
      existingKeys.add(key);
    }

    if (!added.length) {
      this.showToast('info', 'already-selected');
      return;
    }

    this.queue = [...this.queue, ...added];
    if (hasOversized) {
      this.showToast('warning', 'oversize-skipped', { limit: this.singleFileLimitMb });
    }
    this.refreshNotices();
  }

  removeFromQueue(item: QueuedItem): void {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    this.queue = this.queue.filter(it => it !== item);
    this.refreshNotices();
  }

  clearQueue(): void {
    this.queue.forEach(it => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    this.queue = [];
    this.completedBatches = 0;
    this.activeBatchIndex = null;
    this.refreshNotices();
    this.totalBatches = 0;
  }

  cancelUpload(): void {
    if (!this.uploadModalOpened) {
      return;
    }
    const wasSubmitting = this.isSubmitting;
    this.cancelRequested = wasSubmitting;
    this.isSubmitting = false;
    this.isDraggingOver = false;
    this.clearBatchToasts();
    this.clearQueue();
    this.closeUploadModal();
    if (!wasSubmitting) {
      this.cancelRequested = false;
    }
  }

  private summarizeQueue(): QueueSnapshot {
    const valid: QueuedItem[] = [];
    const rejected: QueuedItem[] = [];
    for (const item of this.queue) {
      if (item.status === 'rejected') {
        rejected.push(item);
        continue;
      }
      valid.push(item);
    }

    return {
      valid,
      rejected,
      hasOversize: rejected.some(it => it.rejectionReason === 'oversize'),
    };
  }

  private refreshNotices(existingSnapshot?: QueueSnapshot): { snapshot: QueueSnapshot; batches: QueuedItem[][] } {
    const snapshot = existingSnapshot ?? this.summarizeQueue();
    const batches = this.createBatches(snapshot.valid);
    this.currentBatches = batches;
    this.totalBatches = batches.length;
    this.applyWarnings(snapshot);
    this.updateInformationMessages(batches, snapshot);
    return { snapshot, batches };
  }

  // ---------- Submit (emit one batch) ----------
  async onSubmit(): Promise<void> {
    const snapshotBeforeSubmit = this.summarizeQueue();

    if (!snapshotBeforeSubmit.valid.length) {
      this.showToast('info', 'add-at-least-one');
      return;
    }

    this.cancelRequested = false;
    this.isSubmitting = true;
    this.clearBatchToasts();
    try {
      const { snapshot, batches } = this.refreshNotices(snapshotBeforeSubmit);
      const totalBatches = batches.length;
      this.totalBatches = totalBatches;
      this.completedBatches = 0;
      this.activeBatchIndex = null;
      if (this.uploadModalOpened) {
        this.closeUploadModal();
      }
      const processedAll: File[] = [];

      for (let index = 0; index < totalBatches; index++) {
        if (this.cancelRequested) {
          break;
        }
        const batchItems = batches[index];
        batchItems.forEach(item => {
          item.status = 'processing';
          item.batchIndex = index;
        });
        this.showBatchStartToast(index, totalBatches, batchItems.length);
        this.activeBatchIndex = index;
        this.updateInformationMessages(batches, snapshot);

        const processed = await this.prepareFiles(batchItems.map(item => item.file));
        if (this.cancelRequested) {
          break;
        }
        processedAll.push(...processed);
        this.completedBatches = index + 1;
        this.activeBatchIndex = null;
        this.updateInformationMessages(batches, snapshot);

        const totalSizeMb = batchItems.reduce((acc, item) => acc + item.sizeMb, 0);
        if (!this.cancelRequested) {
          this.batchReady.emit({
            batchIndex: index,
            totalBatches,
            totalSizeMb,
            count: batchItems.length,
            files: processed,
          });

          this.settleBatchToast(index, totalBatches, batchItems.length);
        }
      }

      if (!this.cancelRequested && processedAll.length) {
        this.imagesSelected.emit(processedAll);
        this.showToast('success', 'ready-to-upload', { count: processedAll.length });
      }

      if (!this.cancelRequested) {
        const hasRejected = snapshot.rejected.length > 0;
        if (!hasRejected) {
          this.clearQueue();
        } else {
          this.refreshNotices();
        }
      }
    } catch (e) {
      console.error(e);
      this.showToast('error', 'prepare-failed');
      this.clearBatchToasts();
      this.activeBatchIndex = null;
      this.completedBatches = 0;
      this.refreshNotices();
    } finally {
      this.isSubmitting = false;
      const cancelled = this.cancelRequested;
      if (cancelled) {
        this.cancelRequested = false;
        this.clearBatchToasts();
      }
      if (!this.queue.length) {
        this.completedBatches = 0;
        this.activeBatchIndex = null;
        this.totalBatches = 0;
      }
    }
  }

  private async prepareFiles(files: File[]): Promise<File[]> {
    const out: File[] = [];
    for (const f of files) {
      const mustCompress = this.toMegabytes(f.size) > this.maxSizeInMb;
      if (this.shouldCompress || mustCompress) {
        try {
          const options = {
            maxSizeMB: this.maxSizeInMb,
            maxWidthOrHeight: this.maxWidthOrHeight,
            useWebWorker: true,
          };
          const res = await imageCompression(f, options) as File | Blob;
          const finalFile = res instanceof File
            ? res
            : new File([res], f.name, { type: res.type || f.type, lastModified: f.lastModified });
          out.push(finalFile);
          continue;
        } catch (err) {
          console.warn(`Compression failed for ${f.name}. Using original.`, err);
        }
      }
      out.push(f);
    }
    return out;
  }

  private createBatches(items: QueuedItem[]): QueuedItem[][] {
    if (!items.length) {
      for (const item of this.queue) {
        if (item.status !== 'rejected') {
          item.batchIndex = undefined;
        }
      }
      return [];
    }

    for (const item of this.queue) {
      if (item.status !== 'rejected') {
        item.batchIndex = undefined;
      }
    }

    const batches: QueuedItem[][] = [];
    let current: QueuedItem[] = [];
    let currentSize = 0;

    for (const item of items) {
      if (current.length && currentSize + item.sizeMb > this.batchLimitMb) {
        batches.push(current);
        current = [];
        currentSize = 0;
      }
      current.push(item);
      currentSize += item.sizeMb;
    }

    if (current.length) {
      batches.push(current);
    }

    batches.forEach((batch, batchIndex) => {
      batch.forEach(item => {
        item.batchIndex = batchIndex;
      });
    });

    for (const item of this.queue) {
      if (item.status === 'rejected') {
        item.batchIndex = undefined;
      }
    }

    return batches;
  }

  private applyWarnings(snapshot: QueueSnapshot): void {
    const warnings: string[] = [];

    if (snapshot.hasOversize) {
      warnings.push(
        this.i18n.translate('upload-image.modal.warning.single-too-large', {
          limit: this.singleFileLimitMb,
        })
      );
    }

    const signature = warnings.join('|');
    if (warnings.length && signature !== this.lastWarningSignature) {
      warnings.forEach(message => this.toastr.warning('', message));
    }

    this.lastWarningSignature = warnings.length ? signature : '';
    this.warningMessages = warnings;
  }

  private updateInformationMessages(batches: QueuedItem[][], snapshot: QueueSnapshot): void {
    const messages = batches.map((batch, idx) => {
      const status = this.resolveBatchStatus(batch, idx);
      const size = this.formatSize(batch.reduce((acc, item) => acc + item.sizeMb, 0));
      return this.i18n.translate('upload-image.modal.batch.summary', {
        index: idx + 1,
        count: batch.length,
        size,
        status,
      });
    });

    if (batches.length > 1 && !this.isSubmitting) {
      messages.unshift(
        this.i18n.translate('upload-image.modal.info.batch-plan', {
          count: batches.length,
          limit: this.batchLimitMb,
        })
      );
    }

    if (this.isSubmitting && this.totalBatches > 0) {
      const completed = Math.max(0, Math.min(this.completedBatches, this.totalBatches));
      const inFlight = this.activeBatchIndex !== null;
      const current = Math.min(
        inFlight ? this.activeBatchIndex! + 1 : completed,
        this.totalBatches,
      );
      messages.unshift(
        this.i18n.translate('upload-image.modal.progress.label', {
          current,
          total: this.totalBatches,
        })
      );
    }

    if (!batches.length && snapshot.rejected.length) {
      messages.push(this.i18n.translate('upload-image.modal.info.only-rejected', {
        limit: this.singleFileLimitMb,
      }));
    }

    this.informationMessages = messages;
  }

  private showBatchStartToast(index: number, total: number, count: number): void {
    const toast = this.toastr.info(
      this.i18n.translate('upload-image.modal.toastr.batch-started.content', {
        index: index + 1,
        total,
        count,
      }),
      this.i18n.translate('upload-image.modal.toastr.batch-started.title'),
      { disableTimeOut: true },
    );
    this.batchToastIds.set(index, toast.toastId);
  }

  private settleBatchToast(index: number, total: number, count: number): void {
    const toastId = this.batchToastIds.get(index);
    if (toastId) {
      this.toastr.clear(toastId);
      this.batchToastIds.delete(index);
    }
    this.toastr.success(
      this.i18n.translate('upload-image.modal.toastr.batch-uploaded.content', {
        index: index + 1,
        total,
        count,
      }),
      this.i18n.translate('upload-image.modal.toastr.batch-uploaded.title'),
    );
  }

  private clearBatchToasts(): void {
    this.batchToastIds.forEach(id => this.toastr.clear(id));
    this.batchToastIds.clear();
  }

  private resolveBatchStatus(batch: QueuedItem[], index: number): string {
    if (!batch.length) {
      return this.i18n.translate('upload-image.modal.status.ready');
    }
    if (this.activeBatchIndex === index) {
      return this.i18n.translate('upload-image.modal.status.processing');
    }
    if (index < this.completedBatches) {
      return this.i18n.translate('upload-image.modal.status.uploaded');
    }
    return this.i18n.translate('upload-image.modal.status.ready');
  }

  // ---------- Cleanup ----------
  ngOnDestroy(): void {
    this.clearBatchToasts();
    this.clearQueue();
  }

  private showToast(
    type: 'success' | 'error' | 'info' | 'warning',
    key: string,
    params: Record<string, unknown> = {},
  ): void {
    const content = this.i18n.translate(`upload-image.modal.toastr.${key}.content`, params);
    const title = this.i18n.translate(`upload-image.modal.toastr.${key}.title`, params);
    switch (type) {
      case 'success':
        this.toastr.success(content, title);
        break;
      case 'error':
        this.toastr.error(content, title);
        break;
      case 'info':
        this.toastr.info(content, title);
        break;
      default:
        this.toastr.warning(content, title);
        break;
    }
  }

  formatSize(sizeMb: number): string {
    const sizeKb = sizeMb * 1024;
    if (sizeMb >= 1) {
      const digits = sizeMb >= 10 ? 1 : 2;
      return `${sizeMb.toFixed(digits)} MB`;
    }
    const displayKb = Math.max(sizeKb, 0.1);
    const digits = displayKb >= 10 ? 0 : 1;
    return `${displayKb.toFixed(digits)} KB`;
  }

  statusLabel(item: QueuedItem): string {
    switch (item.status) {
      case 'processing':
        return this.i18n.translate('upload-image.modal.status.processing');
      case 'uploaded':
        return this.i18n.translate('upload-image.modal.status.uploaded');
      case 'rejected':
        if (item.rejectionReason === 'oversize') {
          return this.i18n.translate('upload-image.modal.status.rejected.oversize', {
            limit: this.singleFileLimitMb,
          });
        }
        return this.i18n.translate('upload-image.modal.status.rejected.generic');
      default:
        return this.i18n.translate('upload-image.modal.status.ready');
    }
  }

  statusClass(item: QueuedItem): string {
    switch (item.status) {
      case 'processing':
        return 'status--processing';
      case 'uploaded':
        return 'status--uploaded';
      case 'rejected':
        return 'status--rejected';
      default:
        return 'status--ready';
    }
  }

  progressPercent(): number {
    if (!this.totalBatches) return 0;
    const completed = Math.max(0, Math.min(this.completedBatches, this.totalBatches));
    const inFlight = this.activeBatchIndex !== null;
    const progress = completed + (inFlight ? 0.5 : 0);
    return Math.min(100, Math.max(0, (progress / this.totalBatches) * 100));
  }
}
